import { IToolPanelAngularComp } from '@ag-grid-community/angular'
import {
  Column,
  ColumnApi,
  DragAndDropService,
  DraggingEvent,
  DragSource,
  DragSourceType,
  DropTarget,
  GridApi,
  IToolPanelParams,
} from '@ag-grid-community/core'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  QueryList,
  ViewChildren,
} from '@angular/core'
import { Subscription } from 'rxjs'

import { gridDragAndDropService, resolveMoveIndex } from './column-drag'

/** Which face of the Column Manager is showing. */
export type ColumnManagerMode = 'pick' | 'reorder'

/** Group shown for a column whose colId is absent from the colSource map. */
export const UNGROUPED_LABEL = 'Other'

/** Grid events that invalidate what the panel is showing. */
const REFRESH_EVENTS = [
  'columnVisible',
  'columnMoved',
  'newColumnsLoaded',
  'displayedColumnsChanged',
] as const

export interface WorkbenchColumnManagerParams {
  /** colId -> column-source group label for the grid's current source. */
  columnGroups: () => Record<string, string>
  /** Group labels in the server's display order. Never sorted. */
  groupOrder: () => string[]
}

export type ColumnManagerAgInitParams = WorkbenchColumnManagerParams &
  IToolPanelParams

export interface ColumnManagerItem {
  colId: string
  label: string
  group: string
  visible: boolean
}

export interface ColumnManagerGroup {
  label: string
  /** "visible/total", e.g. "7/7". Counts the whole group, not the search-filtered subset. */
  countLabel: string
  allOn: boolean
  partial: boolean
  collapsed: boolean
  /** Columns rendered under the group — filtered by the search query, sorted by label. */
  items: ColumnManagerItem[]
  /** Every colId in the group, ignoring the search filter. The group checkbox acts on all of them. */
  colIds: string[]
}

/**
 * Column Manager tool panel for the Transactions 2.0 workbench, standing in for ag-Grid's
 * `agColumnsToolPanel` — the 2.0 design asks for grouping and a reorder list that panel has no
 * shape for.
 *
 * Two modes, per the 2.0 design:
 *  - **Pick** groups columns by their column-source, sections laid out in the server's group order
 *    with a `visible/total` count. Sections start collapsed; columns inside one are sorted by label.
 *  - **Reorder** is a flat list in grid order, each row tagged with the group it belongs to.
 *
 * Rows in both modes drag onto the grid; only Reorder also takes a drop back into the list, since
 * Pick's alphabetical order gives a position there no meaning. Dragging goes through ag-Grid's own
 * `DragAndDropService` rather than CDK, because the grid only accepts drops that arrive from it —
 * see `column-drag.ts`.
 *
 * Searching filters columns by label, auto-expands every section and drops sections with no match.
 */
@Component({
  selector: 'hs-workbench-column-manager',
  templateUrl: './workbench-column-manager.component.html',
  styleUrls: ['./workbench-column-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkbenchColumnManagerComponent
  implements IToolPanelAngularComp, AfterViewInit, OnDestroy
{
  mode: ColumnManagerMode = 'pick'
  query = ''
  groups: ColumnManagerGroup[] = []
  reorderItems: ColumnManagerItem[] = []
  ready = false

  /** Drag handles for whichever rows are rendered — Pick's expanded sections, or Reorder's list. */
  @ViewChildren('grip')
  private readonly grips?: QueryList<ElementRef<HTMLElement>>

  private params?: ColumnManagerAgInitParams
  private gridApi?: GridApi
  private columnApi?: ColumnApi
  private dragAndDrop?: DragAndDropService
  private dragSources: DragSource[] = []
  private dropTarget?: DropTarget
  private gripSub?: Subscription
  /** Collapse state per group label. Absent means collapsed, matching the design's default. */
  private readonly collapsedByGroup: Record<string, boolean> = {}
  private readonly onGridEvent = (): void => this.rebuild()

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  agInit(params: ColumnManagerAgInitParams): void {
    this.params = params
    this.gridApi = params.api
    this.columnApi = params.columnApi
    REFRESH_EVENTS.forEach(event =>
      this.gridApi?.addEventListener(event, this.onGridEvent),
    )
    this.dragAndDrop = gridDragAndDropService(params.api)
    this.registerDropTarget()
    this.ready = true
    this.rebuild()
  }

  /** Called by ag-Grid when the panel is re-shown. */
  refresh(): void {
    this.rebuild()
  }

  ngAfterViewInit(): void {
    this.syncDragSources()
    // Switching mode, searching or a grid column change re-renders the rows, and the old handle
    // elements go with them — the drag sources have to be rebound to the new ones.
    this.gripSub = this.grips?.changes.subscribe(() => this.syncDragSources())
  }

  ngOnDestroy(): void {
    REFRESH_EVENTS.forEach(event =>
      this.gridApi?.removeEventListener(event, this.onGridEvent),
    )
    this.gripSub?.unsubscribe()
    this.clearDragSources()
    if (this.dropTarget) this.dragAndDrop?.removeDropTarget(this.dropTarget)
  }

  // ---- user actions ---------------------------------------------------------

  setMode(mode: ColumnManagerMode): void {
    this.mode = mode
    this.rebuild()
  }

  setQuery(event: Event): void {
    this.query = (event.target as HTMLInputElement).value
    this.rebuild()
  }

  toggleCollapse(label: string): void {
    // Absent means collapsed, so the first toggle must expand.
    this.collapsedByGroup[label] = this.collapsedByGroup[label] === false
    this.rebuild()
  }

  toggleColumn(item: ColumnManagerItem): void {
    this.columnApi?.setColumnVisible(item.colId, !item.visible)
    this.rebuild()
  }

  /** Show or hide every column in the group, including any hidden by the current search. */
  toggleGroup(group: ColumnManagerGroup): void {
    this.columnApi?.setColumnsVisible(group.colIds, !group.allOn)
    this.rebuild()
  }

  // ---- drag and drop --------------------------------------------------------

  /**
   * Bind each rendered row's grip as an ag-Grid drag source, the way `agColumnsToolPanel` binds its
   * own rows. The grid's header and body only accept drags carrying `DragSourceType.ToolPanel` from
   * this service (see `BodyDropTarget.isInterestedIn`), so this — not any pointer bookkeeping of
   * our own — is what makes a column dropped on the header actually move.
   */
  private syncDragSources(): void {
    this.clearDragSources()
    const service = this.dragAndDrop
    const columnApi = this.columnApi
    if (!service || !columnApi) return

    this.grips?.forEach(ref => {
      const element = ref.nativeElement
      const colId = element
        .closest('[data-col-id]')
        ?.getAttribute('data-col-id')
      const column = colId ? columnApi.getColumn(colId) : null
      if (!column) return

      const source: DragSource = {
        type: DragSourceType.ToolPanel,
        eElement: element,
        dragItemName: labelOf(column),
        getDragItem: () => ({
          columns: [column],
          visibleState: { [column.getColId()]: column.isVisible() },
        }),
      }
      service.addDragSource(source, true)
      this.dragSources.push(source)
    })
  }

  private clearDragSources(): void {
    this.dragSources.forEach(source =>
      this.dragAndDrop?.removeDragSource(source),
    )
    this.dragSources = []
  }

  /**
   * Make the panel itself a drop target so a row dragged within the list still reorders. Without it
   * the grid is the only place a drag can land, and dropping back on the list would do nothing.
   */
  private registerDropTarget(): void {
    const service = this.dragAndDrop
    if (!service) return

    this.dropTarget = {
      getContainer: () => this.host.nativeElement,
      getIconName: () => DragAndDropService.ICON_MOVE,
      isInterestedIn: type => type === DragSourceType.ToolPanel,
      onDragStop: event => this.onPanelDrop(event),
    }
    service.addDropTarget(this.dropTarget)
  }

  /**
   * Reorder from a drop inside the panel. Pick is sorted alphabetically, so a drop there would have
   * nothing to mean — only Reorder, which shows grid order, acts on it.
   */
  private onPanelDrop(event: DraggingEvent): void {
    if (this.mode !== 'reorder') return

    const colId = event.dragItem.columns?.[0]?.getColId()
    const target = this.rowAt(event.event.clientY)
    if (!colId || !target || target.colId === colId) return

    this.moveColumnBeside(colId, target.colId, target.after)
  }

  /** The Reorder row under a viewport y, and whether the pointer landed past its midline. */
  private rowAt(clientY: number): { colId: string; after: boolean } | null {
    const rows = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('[data-col-id]'),
    )

    for (const row of rows) {
      const rect = row.getBoundingClientRect()
      const colId = row.getAttribute('data-col-id')
      if (colId && clientY >= rect.top && clientY <= rect.bottom) {
        return { colId, after: clientY > rect.top + rect.height / 2 }
      }
    }
    return null
  }

  /**
   * Indices are resolved against the full grid column list rather than the rendered rows, so a drop
   * stays correct while the list is filtered by the search query.
   */
  private moveColumnBeside(
    colId: string,
    targetColId: string,
    after: boolean,
  ): void {
    const colIds = (this.columnApi?.getAllGridColumns() ?? []).map(column =>
      column.getColId(),
    )
    const toIndex = resolveMoveIndex(colIds, colId, targetColId, after)
    if (toIndex === null) return

    this.columnApi?.moveColumn(colId, toIndex)
    this.rebuild()
  }

  trackGroup = (_: number, group: ColumnManagerGroup): string => group.label
  trackItem = (_: number, item: ColumnManagerItem): string => item.colId

  // ---- view model -----------------------------------------------------------

  private rebuild(): void {
    const columns = this.columnApi?.getAllGridColumns() ?? []
    const groupByColId = this.params?.columnGroups() ?? {}
    const query = this.query.trim().toLowerCase()

    const items: ColumnManagerItem[] = columns.map(column => ({
      colId: column.getColId(),
      label: labelOf(column),
      group: groupByColId[column.getColId()] || UNGROUPED_LABEL,
      visible: column.isVisible(),
    }))

    // Reorder keeps grid order — it is the thing being edited, so it must never be sorted.
    this.reorderItems = items.filter(item => matches(item, query))
    this.groups = this.buildGroups(items, query)
    this.cdr.markForCheck()
  }

  private buildGroups(
    items: ColumnManagerItem[],
    query: string,
  ): ColumnManagerGroup[] {
    return this.orderedGroupLabels(items)
      .map(label => {
        const inGroup = items.filter(item => item.group === label)
        if (!inGroup.length) return null

        const shown = inGroup
          .filter(item => matches(item, query))
          .sort((a, b) => a.label.localeCompare(b.label))
        if (query && !shown.length) return null

        const visible = inGroup.filter(item => item.visible).length
        return {
          label,
          countLabel: `${visible}/${inGroup.length}`,
          allOn: visible === inGroup.length,
          partial: visible > 0 && visible < inGroup.length,
          // A search auto-expands every surviving section so matches are visible without clicking.
          collapsed: query ? false : this.collapsedByGroup[label] !== false,
          items: shown,
          colIds: inGroup.map(item => item.colId),
        }
      })
      .filter((group): group is ColumnManagerGroup => group !== null)
  }

  /**
   * The server's group order, then any group a column claims that the server did not list. The
   * fallback keeps an unexpected group visible instead of silently dropping its columns.
   */
  private orderedGroupLabels(items: ColumnManagerItem[]): string[] {
    const serverOrder = this.params?.groupOrder() ?? []
    const known = new Set(serverOrder)
    const extras = items
      .map(item => item.group)
      .filter(group => !known.has(group))
    return [...serverOrder, ...Array.from(new Set(extras))]
  }
}

const labelOf = (column: Column): string =>
  column.getColDef().headerName || column.getColId()

const matches = (item: ColumnManagerItem, query: string): boolean =>
  !query || item.label.toLowerCase().includes(query)
